const API_BASE = process.env.REACT_APP_API_URL || '';

async function request(path, options = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  const rawBody = await response.text();
  const contentType = response.headers.get('content-type') || '';
  const hasJsonBody = contentType.includes('application/json');

  let body = null;
  if (rawBody) {
    const trimmedBody = rawBody.trim();

    if (!hasJsonBody) {
      if (trimmedBody.startsWith('<')) {
        throw new Error('Backend is not reachable. Start the API server on http://localhost:4001.');
      }

      throw new Error(trimmedBody || 'API returned a non-JSON response.');
    }

    try {
      body = JSON.parse(rawBody);
    } catch (_error) {
      throw new Error(`API returned invalid JSON. Raw response: ${trimmedBody.slice(0, 160)}`);
    }
  }

  if (!response.ok) {
    throw new Error(body?.message || 'Request failed.');
  }

  return body;
}

function createQuery(params) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value);
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

export function getProducts() {
  return request('/api/products');
}

export function getProduct(productId) {
  return request(`/api/products/${productId}`);
}

export function signupUser({
  email,
  password,
  fullName,
  phone,
  address,
  locationLabel,
  latitude,
  longitude,
  sessionId,
}) {
  return request('/api/auth/signup', {
    method: 'POST',
    body: JSON.stringify({
      email,
      password,
      fullName,
      phone,
      address,
      locationLabel,
      latitude,
      longitude,
      sessionId,
    }),
  });
}

export function loginUser({ email, password, sessionId }) {
  return request('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, sessionId }),
  });
}

export function getCart({ sessionId, userId }) {
  return request(`/api/cart${createQuery({ sessionId, userId })}`);
}

export function addToCart({ sessionId, userId, productId, quantity = 1 }) {
  return request('/api/cart/items', {
    method: 'POST',
    body: JSON.stringify({ sessionId, userId, productId, quantity }),
  });
}

export function updateCartItem({ itemId, quantity, sessionId, userId }) {
  return request(`/api/cart/items/${itemId}`, {
    method: 'PATCH',
    body: JSON.stringify({ quantity, sessionId, userId }),
  });
}

export function removeCartItem({ itemId, sessionId, userId }) {
  return request(`/api/cart/items/${itemId}${createQuery({ sessionId, userId })}`, {
    method: 'DELETE',
  });
}

export function clearCart({ sessionId, userId }) {
  return request(`/api/cart${createQuery({ sessionId, userId })}`, {
    method: 'DELETE',
  });
}

export function checkoutCart({
  sessionId,
  userId,
  customer,
  shippingAddress,
  paymentMethod,
  notes,
  promoCode,
}) {
  return request('/api/checkout', {
    method: 'POST',
    body: JSON.stringify({
      sessionId,
      userId,
      customer,
      shippingAddress,
      paymentMethod,
      notes,
      promoCode,
    }),
  });
}

export function getOrders({ sessionId, userId }) {
  return request(`/api/orders${createQuery({ sessionId, userId })}`);
}

export function analyzeFaceShape(imageDataUrl) {
  return request('/api/ai/face-shape', {
    method: 'POST',
    body: JSON.stringify({ imageDataUrl }),
  });
}

export function getAdminSummary({ userId }) {
  return request(`/api/admin/summary${createQuery({ userId })}`);
}

export function getAdminUsers({ userId }) {
  return request(`/api/admin/users${createQuery({ userId })}`);
}

export function deleteAdminUser({ userId, targetUserId }) {
  return request(`/api/admin/users/${targetUserId}${createQuery({ userId })}`, {
    method: 'DELETE',
  });
}

export function createAdminProduct({ userId, product }) {
  return request('/api/admin/products', {
    method: 'POST',
    body: JSON.stringify({ userId, ...product }),
  });
}

export function updateAdminProduct({ userId, productId, product }) {
  return request(`/api/admin/products/${productId}`, {
    method: 'PATCH',
    body: JSON.stringify({ userId, ...product }),
  });
}

export function deleteAdminProduct({ userId, productId }) {
  return request(`/api/admin/products/${productId}${createQuery({ userId })}`, {
    method: 'DELETE',
  });
}

export function getAccount({ userId }) {
  return request(`/api/account${createQuery({ userId })}`);
}

export function updateAccount({ userId, updates }) {
  return request('/api/account', {
    method: 'PATCH',
    body: JSON.stringify({ userId, ...updates }),
  });
}

export function getAppointments({ userId }) {
  return request(`/api/appointments${createQuery({ userId })}`);
}

export function createAppointment({ userId, appointment }) {
  // Allow guest booking: only send `userId` when available.
  const payload = {
    ...(userId ? { userId } : {}),
    ...appointment,
  };

  return request('/api/appointments', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getAdminOrders({ userId, date }) {
  return request(`/api/admin/orders${createQuery({ userId, date })}`);
}

export function deleteAdminOrder({ userId, orderId }) {
  return request(`/api/admin/orders/${orderId}${createQuery({ userId })}`, {
    method: 'DELETE',
  });
}

export function updateAdminOrderStatus({ userId, orderId, status }) {
  return request(`/api/admin/orders/${orderId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ userId, status }),
  });
}

export function getAdminAppointments({ userId, date }) {
  return request(`/api/admin/appointments${createQuery({ userId, date })}`);
}

export function deleteAdminAppointment({ userId, appointmentId }) {
  return request(`/api/admin/appointments/${appointmentId}${createQuery({ userId })}`, {
    method: 'DELETE',
  });
}

export function updateAdminAppointment({ userId, appointmentId, date, time }) {
  return request(`/api/admin/appointments/${appointmentId}`, {
    method: 'PATCH',
    body: JSON.stringify({ userId, date, time }),
  });
}

export function getAdminPromoCodes({ userId }) {
  return request(`/api/admin/promo-codes${createQuery({ userId })}`);
}

export function createAdminPromoCode({ userId, promoCode }) {
  return request('/api/admin/promo-codes', {
    method: 'POST',
    body: JSON.stringify({ userId, ...promoCode }),
  });
}

export function deleteAdminPromoCode({ userId, promoCodeId }) {
  return request(`/api/admin/promo-codes/${promoCodeId}${createQuery({ userId })}`, {
    method: 'DELETE',
  });
}

export function validatePromoCode({ sessionId, userId, code }) {
  return request('/api/promo-codes/validate', {
    method: 'POST',
    body: JSON.stringify({ sessionId, userId, code }),
  });
}

export function createContactMessage(contactMessage) {
  return request('/api/contact-messages', {
    method: 'POST',
    body: JSON.stringify(contactMessage),
  });
}

export function getAdminContactMessages({ userId }) {
  return request(`/api/admin/contact-messages${createQuery({ userId })}`);
}

export function getAdminBills({ userId }) {
  return request(`/api/admin/bills${createQuery({ userId })}`);
}

export function createAdminBill({ userId, bill }) {
  return request('/api/admin/bills', {
    method: 'POST',
    body: JSON.stringify({ userId, ...bill }),
  });
}

export function deleteAdminBill({ userId, billId }) {
  return request(`/api/admin/bills/${billId}${createQuery({ userId })}`, {
    method: 'DELETE',
  });
}

export function updateAdminBillPayment({ userId, billId, paidAmount, paymentMethod, isFullyPaid }) {
  return request(`/api/admin/bills/${billId}/payment`, {
    method: 'PATCH',
    body: JSON.stringify({
      userId,
      ...(typeof paidAmount === 'number' ? { paidAmount } : {}),
      ...(paymentMethod ? { paymentMethod } : {}),
      ...(typeof isFullyPaid === 'boolean' ? { isFullyPaid } : {}),
    }),
  });
}

export function getAdminSalesRecords({ userId, period, day, month }) {
  return request(
    `/api/admin/sales-records${createQuery({
      userId,
      period,
      day,
      month,
    })}`
  );
}

export function getAdminExpenses({ userId, day }) {
  return request(`/api/admin/expenses${createQuery({ userId, day })}`);
}

export function createAdminExpense({ userId, expense }) {
  return request('/api/admin/expenses', {
    method: 'POST',
    body: JSON.stringify({ userId, ...expense }),
  });
}

export function deleteAdminExpense({ userId, expenseId }) {
  return request(`/api/admin/expenses/${expenseId}${createQuery({ userId })}`, {
    method: 'DELETE',
  });
}

export function migrateAdminBillPayments({ userId }) {
  return request('/api/admin/migrate-bill-payments', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  });
}
